'use client';

import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * One row that always fits — by measuring, not by guessing a breakpoint.
 *
 * The bars on this page used to be laid out by screen width: three language
 * tabs from `md` up, two below it, the tagline from `sm`. That is a guess about
 * what fits, and it was wrong in both directions — a 1000px window wrapped the
 * language bar onto two lines while a phone held landscape hid tabs it had
 * room for. A row does not care how wide the SCREEN is; it cares how wide the
 * row is and how wide the things in it are (which moves with the font, the
 * language names, the account, a "Detected" suffix …).
 *
 * So the row says what it can live without, in order, and this hook takes
 * those away one at a time until what is left fits on one line — again every
 * time the row or anything in it changes size:
 *
 *   data-fit="key"      a thing that can go; hidden once `key` is dropped
 *   data-fit-alt="key"  its stand-in; shown ONLY once `key` is dropped
 *                       (a two-line wordmark for a one-line one)
 *   data-fit-more       shown once anything at all is dropped (a ▾ that lists
 *                       what went)
 *
 * `drop` is the keys, first to go first. A key with nothing on screen is
 * skipped. Whatever is not in `drop` never goes — so the selected tab is kept
 * simply by leaving it out. If everything droppable is gone and the row still
 * overflows, the row gets `data-fit-tight`, which the things that are left can
 * answer by truncating (`[[data-fit-tight]_&]:min-w-0`).
 *
 * `refill` is for a row of equals (tabs): once the row fits, anything that
 * went is tried again, most wanted first, and kept if there turns out to be
 * room after all — so a long "Chinese (Simplified)" that had to go does not
 * take a short "Spanish" with it. A row whose order is a real priority (the
 * header) leaves it off: there, nothing comes back ahead of what outranks it.
 *
 * The row must be a single-line flex row (no `flex-wrap`) whose items do not
 * shrink or wrap on their own — an item that quietly squeezes is an overflow
 * this cannot see.
 *
 * Shown and hidden with inline `display`, straight on the element and before
 * the browser paints, so nothing is ever drawn overflowing first. `dropped` is
 * the same answer as state, for a row that has to SAY what went (the ▾ menu).
 */
export function useFit<T extends HTMLElement>(
  drop: readonly string[],
  /** Anything that changes what is in the row — re-fit when it changes. */
  sig: string = '',
  { refill = false }: { refill?: boolean } = {},
): { ref: RefObject<T>; dropped: string[] } {
  const ref = useRef<T>(null);
  const [dropped, setDropped] = useState<string[]>([]);
  const order = drop.join('\n');

  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    box.setAttribute('data-fit-box', '');

    /** This row's own markers — not those of a fitted row nested inside it. */
    const marked = () =>
      [...box.querySelectorAll<HTMLElement>('[data-fit],[data-fit-alt],[data-fit-more]')]
        .filter((el) => el.parentElement?.closest('[data-fit-box]') === box);

    /**
     * Does the row's content run past its right edge? Read from the in-flow
     * items rather than `scrollWidth`, which also counts a popover hanging off
     * the row — an open language panel must not read as "the tabs do not fit".
     */
    const over = () => {
      const cs = getComputedStyle(box);
      const edge = box.getBoundingClientRect().right - parseFloat(cs.paddingRight) - parseFloat(cs.borderRightWidth);
      let right = -Infinity;
      const walk = (el: Element) => {
        for (const c of el.children) {
          const s = getComputedStyle(c);
          if (s.display === 'none' || s.position === 'absolute' || s.position === 'fixed') continue;
          if (s.display === 'contents') { walk(c); continue; }
          right = Math.max(right, c.getBoundingClientRect().right + parseFloat(s.marginRight));
        }
      };
      walk(box);
      return right > edge + 0.5;
    };

    const fit = () => {
      const els = marked();
      const gone = new Set<string>();
      const paint = () => {
        for (const el of els) {
          const { fit: key, fitAlt: alt, fitMore: more } = el.dataset;
          const on = !(key && gone.has(key)) && !(alt && !gone.has(alt)) && !(more !== undefined && !gone.size);
          el.style.display = on ? '' : 'none';
        }
      };
      box.removeAttribute('data-fit-tight');
      paint();
      for (const key of order ? order.split('\n') : []) {
        if (!over()) break;
        if (!els.some((el) => el.dataset.fit === key)) continue;
        gone.add(key);
        paint();
      }
      if (over()) box.setAttribute('data-fit-tight', '');
      else if (refill) {
        for (const key of [...gone].reverse()) {
          gone.delete(key);
          paint();
          if (over()) { gone.add(key); paint(); }
        }
      }
      const next = [...gone];
      setDropped((prev) => (prev.join('\n') === next.join('\n') ? prev : next));
    };

    fit();
    // The row itself (the window was resized) and everything in it (the font
    // changed, a label grew a "- Detected", a balance loaded).
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    for (const c of box.children) ro.observe(c);
    for (const el of marked()) ro.observe(el);
    return () => ro.disconnect();
  }, [order, sig, refill]);

  return { ref, dropped };
}
